import React, { useContext, useMemo } from 'react';

import { ConditionalRendering } from '@components/common/conditional-rendering';
import { CurrencyInput, ValidationErrorMessage } from '@components/common/form';
import { INPUT_TYPES } from '@components/common/form/constants/fieldTypes';
import Tooltip from '@components/common/tooltip/Tooltip';
import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';
import { usePermissions } from '@hooks/usePermissions';
import useValidateSubsidyAmount from '@hooks/useValidateSubsidyAmount';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { currencyChange } from '../../common/enrollmentUtils';
import { IEnrollmentPremiumDto } from '../../common/types';
import { EnrollmentContext } from '../../context/MemberMyEnrollmentProvider';
import { StyledField } from '../../styles/EnrollmentSharedStyled';

const ColumnStateSubsidyAmount = ({ premium }: { premium: IEnrollmentPremiumDto }) => {
    const intl = useIntl();
    const permissions = useSelector((state: any) => state.memberEnrollment?.permissions);
    const { userPermissions } = permissions || {};
    const { viewEditableFields } = usePermissions();
    const validateSubsidy = useValidateSubsidyAmount();
    const { enrollmentPremiumFormContext, selectedEnrollment } = useContext(EnrollmentContext);
    const {
        watch,
        trigger,
        setValue,
        formState: { errors },
    } = enrollmentPremiumFormContext;
    const values = watch();
    const { isEnrollmentPendingOrPendCancelled } = usePermissions();

    const onStateSubsidyChange = e => {
        validateSubsidy({ ...premium, stateSubsidyAmount: Number(parseFloat(e.target.value).toFixed(2)) });
        currencyChange(setValue, 'stateSubsidyAmount', premium, e.target.value);
        trigger([`enrollmentPremiumDtoList.${premium.month - 1}`]);
    };

    const isDisabled = useMemo(
        () =>
            !premium.isFinancial ||
            (!selectedEnrollment.isStateSubsidyEnabled && premium.stateSubsidyAmount <= 0) ||
            !userPermissions?.memberEnrlmntAptcEditable ||
            isEnrollmentPendingOrPendCancelled(selectedEnrollment),
        [premium.isFinancial],
    );

    return (
        <ConditionalRendering
            isTrue={selectedEnrollment.isStateSubsidyEnabled && viewEditableFields(userPermissions, values, premium)}
        >
            <StyledField>
                <CurrencyInput
                    // @ts-ignore
                    label={{
                        wrapperClasses: [''],
                        labelClasses: [''],
                        labelText: '',
                        showRequired: false,
                        isInputRequired: false,
                    }}
                    input={{
                        id: 'stateSubsidyAmount',
                        isInputRequired: true,
                        type: INPUT_TYPES.TEXT,
                        value: premium.stateSubsidyAmount,
                        disabled: isDisabled,
                        dataTestId: 'stateSubsidyAmount',
                        name: 'stateSubsidyAmount',
                        placeholder: '',
                    }}
                    actions={{
                        handleChange: e => onStateSubsidyChange(e),
                    }}
                    maskOptions={{
                        allowDecimal: true,
                        prefix: '',
                        decimalLimit: '2',
                        integerLimit: 10,
                        includeThousandsSeparator: false,
                        allowLeadingZeroes: true,
                    }}
                    showIcon={true}
                />

                <ConditionalRendering isTrue={!premium.isFinancial}>
                    <Tooltip
                        index={`stateSubsidyAmountTooltip-${premium.month}`}
                        id="stateSubsidyAmountTooltip"
                        elementId="stateSubsidyAmountTooltip"
                        content="member.enrollment_list.premiumhistory.tooltip.maxStateSubsidy"
                        place="top"
                        icon={faQuestionCircle}
                        dataAutomationId="stateSubsidyAmountTooltip"
                    />
                </ConditionalRendering>
            </StyledField>
            <ConditionalRendering isTrue={premium.isFinancial}>
                <ValidationErrorMessage
                    isVisible={errors?.enrollmentPremiumDtoList?.[premium.month - 1]?.stateSubsidyAmount}
                    messageText={errors?.enrollmentPremiumDtoList?.[premium.month - 1]?.stateSubsidyAmount?.message}
                />
            </ConditionalRendering>
        </ConditionalRendering>
    );
};

export default ColumnStateSubsidyAmount;
